import { PageWrapper } from '../components/layout/PageWrapper'
import { Seo } from '../components/Seo'

export function PrivacyPolicy() {
  return (
    <PageWrapper>
      <Seo
        title="Privacy Policy"
        description="How FixDelivered collects, uses, and protects your personal data."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Legal</p>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-400 mb-10">Last updated: March 2026</p>

        <div className="prose-sm space-y-8 text-gray-600 leading-relaxed">

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Who we are</h2>
            <p>RLowe Limited ("we", "us", "our") operates the FixDelivered platform at fixdelivered.com. We are registered in England and Wales. We act as the data controller for personal information collected through this website and our services.</p>
            <p className="mt-2">If you have any questions about this policy or how we handle your data, contact us at <a href="mailto:privacy@fixdelivered.com" className="text-gray-900 underline">privacy@fixdelivered.com</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. What information we collect</h2>
            <p className="font-medium text-gray-700">If you are a customer:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Contact details</strong> - your phone number, and optionally your email address and full address, provided when submitting a job request or signing in.</li>
              <li><strong>Job information</strong> - description of the issue, postcode, and any details you provide about your property or situation.</li>
              <li><strong>Payment information</strong> - we use Stripe to process payments. We do not store your card details; Stripe holds these under their own privacy policy.</li>
              <li><strong>Usage data</strong> - pages visited, device type, browser, IP address, and referral source, collected automatically via cookies and analytics tools.</li>
              <li><strong>Communications</strong> - records of SMS messages and any correspondence you send us.</li>
            </ul>
            <p className="font-medium text-gray-700 mt-4">If you are a plumber:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Business information</strong> - company name, trading address, phone number, website, and areas of operation.</li>
              <li><strong>Qualifications and certifications</strong> - Gas Safe Register number, TrustMark registration, WaterSafe membership, and other trade credentials.</li>
              <li><strong>Profile information</strong> - biography, specialties, photos, working hours, and availability status set through the portal.</li>
              <li><strong>Reviews and ratings</strong> - publicly available ratings and review summaries sourced from third-party platforms and customer feedback.</li>
              <li><strong>Portal activity</strong> - login events, profile updates, and booking responses.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. How we use your information</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To match customers with available plumbers and facilitate the booking process.</li>
              <li>To contact plumbers on your behalf using automated voice calls and messages as part of the emergency matching service.</li>
              <li>To contact you via SMS or email with updates about your job request or booking.</li>
              <li>To process payments and issue refunds where applicable.</li>
              <li>To provide your contact details and address to the confirmed plumber so they can attend.</li>
              <li>To display plumber profiles and directory listings publicly to help customers find tradespeople.</li>
              <li>To generate and maintain plumber profile descriptions using AI tools, based on publicly available information about the business.</li>
              <li>To improve our service and investigate complaints or disputes.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Legal basis for processing</h2>
            <p>We process your data on the following grounds under UK GDPR:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Contract</strong> - processing necessary to fulfil the service you have requested.</li>
              <li><strong>Legitimate interests</strong> - improving our platform, fraud prevention, service communications, and maintaining an accurate directory of tradespeople.</li>
              <li><strong>Legal obligation</strong> - where we are required to retain records by law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Who we share your data with</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Confirmed plumbers</strong> - once a plumber is matched and confirmed, we share your name, address, phone number, and job description with them so they can attend.</li>
              <li><strong>Stripe</strong> - for payment processing.</li>
              <li><strong>Customer.io</strong> - for transactional SMS and email delivery.</li>
              <li><strong>Bland.ai</strong> - for automated voice calls made to plumbers during the emergency matching process. Job details are passed to this service to facilitate the call.</li>
              <li><strong>Twilio</strong> - for automated text messages sent to plumbers during the emergency matching process (an alternative to a voice call, used for some jobs). Job details are passed to this service to facilitate the message.</li>
              <li><strong>Google Cloud</strong> - for hosting and infrastructure.</li>
              <li><strong>Firebase (Google)</strong> - for user authentication.</li>
              <li><strong>Google AI (Gemini)</strong> - for generating plumber profile descriptions. Plumber business information may be processed by this service.</li>
              <li><strong>Google Places API</strong> - for sourcing publicly available reviews and ratings associated with plumber businesses.</li>
            </ul>
            <p className="mt-2">We do not sell your personal data to third parties.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Plumber directory and public profiles</h2>
            <p>Plumber profiles in the FixDelivered directory are publicly visible and may include business name, location, phone number, qualifications, reviews, ratings, and AI-generated descriptions. This information is sourced from publicly available trade registers, third-party directories, and directly from plumbers who have claimed their profile.</p>
            <p className="mt-2">If you are a plumber and wish to update or remove your listing, please contact us at <a href="mailto:privacy@fixdelivered.com" className="text-gray-900 underline">privacy@fixdelivered.com</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. How long we keep your data</h2>
            <p>We retain job records and associated personal data for up to 3 years for dispute resolution and legal compliance purposes. Authentication data is retained while your account is active. Plumber directory data is retained until a removal request is received or the listing is no longer accurate. You may request deletion at any time (see section 9).</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">8. Cookies</h2>
            <p>We use essential cookies required for the platform to function (e.g. authentication tokens) and analytics cookies to understand how our service is used. You can disable non-essential cookies in your browser settings, though this may affect functionality.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">9. Your rights</h2>
            <p>Under UK GDPR you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your data ("right to be forgotten").</li>
              <li>Object to or restrict certain processing.</li>
              <li>Data portability - receive your data in a structured, machine-readable format.</li>
              <li>Lodge a complaint with the ICO (<a href="https://ico.org.uk" className="text-gray-900 underline">ico.org.uk</a>).</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, email <a href="mailto:privacy@fixdelivered.com" className="text-gray-900 underline">privacy@fixdelivered.com</a>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">10. Changes to this policy</h2>
            <p>We may update this policy from time to time. Material changes will be notified via the email address on your account where possible. Continued use of the service after changes constitutes acceptance of the updated policy.</p>
          </section>

        </div>
      </div>
    </PageWrapper>
  )
}
